import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@/config/firebase.config';

/**
 * Upload a user profile photo
 */
export const uploadProfilePhoto = async (userId: string, file: File): Promise<string> => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    const storageRef = ref(storage, `profile-photos/${userId}`);
    await uploadBytes(storageRef, file);

    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

/**
 * Delete a user profile photo
 */
export const deleteProfilePhoto = async (userId: string): Promise<void> => {
  try {
    const storageRef = ref(storage, `profile-photos/${userId}`);
    await deleteObject(storageRef);
  } catch (error) {
    // Don't throw error if file doesn't exist
    if (error && typeof error === 'object' && 'code' in error) {
      if (error.code !== 'storage/object-not-found') {
        console.error('Error deleting profile photo:', error);
        throw error;
      }
    }
  }
};

/**
 * Upload a service image
 */
export const uploadServiceImage = async (
  serviceId: string,
  file: File,
  index: number = 0
): Promise<string> => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    const storageRef = ref(storage, `service-images/${serviceId}/${index}`);
    await uploadBytes(storageRef, file);

    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading service image:', error);
    throw error;
  }
};

/**
 * Delete a service image
 */
export const deleteServiceImage = async (
  serviceId: string,
  index: number = 0
): Promise<void> => {
  try {
    const storageRef = ref(storage, `service-images/${serviceId}/${index}`);
    await deleteObject(storageRef);
  } catch (error) {
    // Don't throw error if file doesn't exist
    if (error && typeof error === 'object' && 'code' in error) {
      if (error.code !== 'storage/object-not-found') {
        console.error('Error deleting service image:', error);
        throw error;
      }
    }
  }
};
